import { IonContent, IonPage, IonText, IonIcon, IonSkeletonText, IonNavLink, useIonToast } from '@ionic/react'
import { alertCircleOutline } from 'ionicons/icons'
import { observer } from 'mobx-react-lite'
import { useContext, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { amountString, isLoaded, onCopyContent, ResProductDetail, VALIDATE_STATUS_TRADE } from '../../../../common'
import {
  CustomSpacer,
  FooterButtons,
  StyleIonHeader,
  StyleIonInputNew,
  StyleIonInputValue,
  TextStyle,
} from '../../../../components'
import { DivideLine, DivideLineBold } from '../../../../components/atoms/divide'
import { StoresContext } from '../../../../provider/StoreProvider'
import { LoadingModal } from './loadingModal'
import { TradeAgreeModal } from './TradeAgreeModal'

type Props = {
  productData: ResProductDetail
  currentCost?: number
  orderType: 'BUY' | 'SELL'
  onDismiss: () => void
}

export const TradeOrderModal: React.FC<Props> = observer(({ productData, currentCost, orderType, onDismiss }) => {
  const [totalCost, setTotalCost] = useState<number>(0)
  const [validateStatus, setValidateStatus] = useState<VALIDATE_STATUS_TRADE>(VALIDATE_STATUS_TRADE.INPUT)
  const { commonStore, myStore, authStore } = useContext(StoresContext)
  const balance = commonStore.currentBalance.content?.balance ?? 0
  const contents = useRef<HTMLIonContentElement>(null)
  const [cost, setCost] = useState(currentCost ? currentCost : '')
  const [presentToast] = useIonToast()
  const [inputValues, setInputValues] = useState({
    count: 0,
  })
  const { count } = inputValues
  const detailStore = myStore.depositAccountStore

  useEffect(() => {
    commonStore.currentBalance.load()
    detailStore.load(undefined, true)
  }, [])

  const onChangeInput = (event: { target: { value: string | number; name: string | number } }) => {
    const { value, name } = event.target
    setInputValues({ ...inputValues, [name]: value })
  }

  const getOrderMessage = (): string => {
    switch (validateStatus) {
      case VALIDATE_STATUS_TRADE.OKAY:
        return `${orderType === 'BUY' ? '구매' : '판매'} 주문하기`
      default:
        return validateStatus ?? ''
    }
  }

  const onInputCost = (cost: number | string) => {
    setCost(cost as number)
  }

  const validate = () => {
    const ownCount = productData.ownCount ? productData.ownCount : 0
    const feeRate = 0.011
    const total = Number(cost) * Number(count)
    let totalWithVat = total * (1 + feeRate)
    totalWithVat = totalWithVat == null || isNaN(totalWithVat) || totalWithVat <= 0 ? 0 : totalWithVat
    setTotalCost(Math.floor(totalWithVat))
    if (authStore.userInfo == null) {
      setValidateStatus(VALIDATE_STATUS_TRADE.SIGN_IN)
    } else if (totalWithVat <= 0) {
      setValidateStatus(VALIDATE_STATUS_TRADE.INPUT)
    } else if (orderType === 'BUY' && isLoaded(balance) && totalWithVat > Number(balance)) {
      setValidateStatus(VALIDATE_STATUS_TRADE.BALANCE)
    } else if (orderType === 'SELL' && count > ownCount) {
      setValidateStatus(VALIDATE_STATUS_TRADE.OWN_COUNT)
    } else {
      setValidateStatus(VALIDATE_STATUS_TRADE.OKAY)
    }
  }

  useEffect(() => {
    validate()
  }, [cost, count])

  return (
    <IonPage>
      <StyleIonHeader onDismiss={onDismiss} title={orderType === 'BUY' ? '주문' : '판매'} type={'MODAL'} />
      <IonContent ref={contents}>
        <ProductContainer>
          <ProductInfoContainer>
            <img
              src={productData.imageUri}
              alt={productData.name}
              onError={({ currentTarget }) => {
                currentTarget.onerror = null
                currentTarget.src = '/img/thumbnail.svg'
              }}
            />
            <div className="product-info">
              <IonText className="artist">{productData.artist}</IonText>
              <IonText className="title">{productData.name}</IonText>
            </div>
          </ProductInfoContainer>
        </ProductContainer>
        <DivideLineBold />
        {/* 보유 자산 판매 */}
        {orderType === 'SELL' && (
          <>
            <SectionContainer>
              <TextStyle type={'b_16'}>보유수량</TextStyle>
              <BetweenWrap>
                <TextStyle type={'r_13'}>보유 조각 수량</TextStyle>
                <TextStyle type={'b_15'}>{productData.ownCount ?? 0} 개</TextStyle>
              </BetweenWrap>
            </SectionContainer>
            <DivideLineBold />
            <SectionContainer>
              <TextStyle type={'b_16'}>총 판매 금액</TextStyle>
              <BetweenWrap>
                <div className="text_wrap_left">
                  <TextStyle type={'r_13'}>판매 수량</TextStyle>
                </div>
                <StyleIonInputNew
                  isRightText={true}
                  type={'number'}
                  inputType={'INPUT'}
                  inputStyle={'BOX'}
                  placeholder="수량을 입력해주세요"
                  onInput={onChangeInput}
                  name={'count'}
                />
                <div className="text_wrap_right">
                  <TextStyle type={'b_13'}>개</TextStyle>
                </div>
              </BetweenWrap>
              <BetweenWrap>
                <div className="text_wrap_left">
                  <TextStyle type={'r_13'}>단가 금액</TextStyle>
                </div>
                <StyleIonInputValue
                  isRightText={true}
                  type={'number'}
                  inputType={'INPUT'}
                  inputStyle={'BOX'}
                  placeholder="가격을 입력해주세요"
                  onInput={onInputCost}
                  value={cost}
                />
                <div className="text_wrap_right">
                  <TextStyle type={'b_13'}>원</TextStyle>
                </div>
              </BetweenWrap>
              <CustomSpacer spacing="16px" bottomLine={true} />
              <BetweenWrap>
                <TextStyle type={'b_15'}>
                  판매 예정 금액
                  <br />
                  (수수료 1.1% 포함)
                </TextStyle>
                <TextStyle type={'b_16'}>{`${amountString(String(totalCost))} 원`}</TextStyle>
              </BetweenWrap>
            </SectionContainer>
          </>
        )}

        {/*  자산 구매 */}
        {orderType === 'BUY' && (
          <>
            <SectionContainer>
              <TextStyle type={'b_16'}>결제정보</TextStyle>
              <BetweenWrap>
                <TextStyle type={'r_13'}>결제 수단</TextStyle>
                <TextStyle type={'b_15'}>예치금</TextStyle>
              </BetweenWrap>
              <CustomSpacer spacing="16px" />
              <DivideLine />
              <BetweenWrap>
                <div className="left_items">
                  <TextStyle type={'r_13'}>예치금 총 금액</TextStyle>
                  <IonIcon icon={alertCircleOutline}></IonIcon>
                </div>
                <TextStyle type={'b_15'}>
                  {commonStore.currentBalance.isLoading && (
                    <IonSkeletonText animated={true} style={{ width: '100px' }} />
                  )}
                  {commonStore.currentBalance.isFailed && '??'}
                  {isLoaded(balance) && `${balance.toLocaleString()} 원`}
                </TextStyle>
              </BetweenWrap>
              <BetweenWrap>
                <div className="left_items">
                  <TextStyle type={'r_13'} color={'secondary_main'}>
                    거래 가능 금액
                  </TextStyle>
                  <IonIcon icon={alertCircleOutline}></IonIcon>
                </div>
                <TextStyle type={'b_15'}>
                  {commonStore.currentBalance.isLoading && (
                    <IonSkeletonText animated={true} style={{ width: '100px' }} />
                  )}
                  {commonStore.currentBalance.isFailed && '??'}
                  {isLoaded(balance) && `${amountString(String(balance))} 원`}
                </TextStyle>
              </BetweenWrap>
            </SectionContainer>
            <DivideLineBold />
            <SectionContainer>
              <TextStyle type={'b_16'}>최종 결제 금액</TextStyle>
              <BetweenWrap>
                <div className="text_wrap_left">
                  <TextStyle type={'r_13'}>구매 수량</TextStyle>
                </div>
                <StyleIonInputNew
                  isRightText={true}
                  type={'number'}
                  inputType={'INPUT'}
                  inputStyle={'BOX'}
                  placeholder="수량을 입력해주세요"
                  onInput={onChangeInput}
                  name={'count'}
                />
                <div className="text_wrap_right">
                  <TextStyle type={'b_15'}>개</TextStyle>
                </div>
              </BetweenWrap>
              <BetweenWrap>
                <div className="text_wrap_left">
                  <TextStyle type={'r_13'}>단가</TextStyle>
                </div>
                <StyleIonInputValue
                  isRightText={true}
                  type={'number'}
                  inputType={'INPUT'}
                  inputStyle={'BOX'}
                  placeholder="가격을 입력해주세요"
                  onInput={onInputCost}
                  value={cost}
                />
                <div className="text_wrap_right">
                  <TextStyle type={'b_15'}>원</TextStyle>
                </div>
              </BetweenWrap>
              <CustomSpacer spacing="16px" bottomLine={true} />
              <CustomSpacer spacing="16px" />
              <BetweenWrap>
                <TextStyle type={'b_16'}>
                  결제 예정 금액 <br />
                  (수수료 1.1% 포함)
                </TextStyle>
                <TextStyle type={'b_16'}>{`${amountString(String(totalCost), false, 0)} 원`}</TextStyle>
              </BetweenWrap>
            </SectionContainer>
          </>
        )}
      </IonContent>
      {validateStatus === VALIDATE_STATUS_TRADE.BALANCE && (
        <InfoContainer>
          <p
            onClick={() => {
              onCopyContent(presentToast, {
                content: String(detailStore.content?.account),
                successMsg: '가상 계좌 번호가 복사되었습니다!',
                failureMsg: '가상 계좌 번호 복사에 실패했습니다.',
              })
            }}
          >
            {detailStore.isLoaded && (
              <>
                <span>예치금 충전 계좌 정보</span>
                <span>
                  은행명: {detailStore.content?.bankName ?? '-'} / 예금주: {detailStore.content?.bankOwner ?? '-'}
                </span>
                <span>계좌번호: {detailStore.content?.account ?? '-'}</span>
              </>
            )}
          </p>
        </InfoContainer>
      )}
      {validateStatus === VALIDATE_STATUS_TRADE.OKAY && orderType === 'BUY' && (
        <IonNavLink
          routerDirection="forward"
          component={() => (
            <TradeAgreeModal
              productData={productData}
              orderType={orderType}
              onDismiss={onDismiss}
              orderCost={cost as number}
              orderCount={count as number}
            />
          )}
        >
          <FooterButtons button={{ color: 'primary', text: getOrderMessage() }} />
        </IonNavLink>
      )}
      {validateStatus === VALIDATE_STATUS_TRADE.OKAY && orderType === 'SELL' && (
        <IonNavLink
          routerDirection="forward"
          component={() => (
            <LoadingModal
              type={'TRADE'}
              onDismiss={onDismiss}
              tradeData={{
                productData: productData,
                cost: cost as number,
                count: count,
                orderType: orderType,
              }}
            />
          )}
        >
          <FooterButtons button={{ color: 'danger', text: getOrderMessage() }} />
        </IonNavLink>
      )}
      {validateStatus !== VALIDATE_STATUS_TRADE.OKAY && (
        <FooterButtons
          button={{
            color: orderType === 'BUY' ? 'primary' : 'danger',
            text: getOrderMessage(),
            disabled: validateStatus === VALIDATE_STATUS_TRADE.SIGN_IN ? false : true,
            onClick: () => {
              return validateStatus === VALIDATE_STATUS_TRADE.SIGN_IN && location.replace('/signIn')
            },
          }}
        />
      )}
    </IonPage>
  )
})

const ProductContainer = styled.div`
  padding: 24px 20px;
`

const ProductInfoContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  grid-template-rows: 1fr;
  grid-column-gap: 20px;
  grid-row-gap: 0px;
  img {
    border-radius: 7px;
    min-width: 80px;
    width: 100%;
    aspect-ratio: 1/1;
    object-fit: cover;
  }
  .product-info {
    grid-area: 1 / 1 / 2 / 5;
    display: flex;
    flex-direction: column;
    font-size: 15px;
    .artist {
      white-space: nowrap;
      color: var(--ion-color-primary_light);
      font-size: 13px;
      overflow: hidden;
      text-overflow: ellipsis;
    }
    .title {
      overflow: hidden;
      text-overflow: ellipsis;
      display: -webkit-box;
      -webkit-line-clamp: 2;
      -webkit-box-orient: vertical;
      word-wrap: break-word;
      font-size: 16px;
      font-weight: 700;
    }
  }
`

const SectionContainer = styled.div`
  padding: 24px 20px;
`

const BetweenWrap = styled.div`
  margin-top: 16px;
  display: flex;
  justify-content: space-between;
  .left_items {
    display: flex;
    align-items: center;
    ion-icon {
      margin-left: 6px;
    }
  }
  .text_wrap_left {
    width: 80px;
    margin: auto 0;
  }
  .text_wrap_right {
    width: 20px;
    text-align: end;
    margin: auto 0;
  }
`

const InfoContainer = styled.footer`
  padding: 8px 20px;
  display: flex;
  flex-direction: column;
  height: 89px;
  p {
    display: flex;
    flex-direction: column;
    color: var(--ion-color-success_main);
    text-align: center;
    font-size: 14px;
    margin: auto 0;
  }
`
